// AISettingsPanel.jsx - AI Configuration Panel for Organizations and Users
// Configure Kubi & Spirko AI assistant preferences with dark mode support

import React, { useState, useEffect } from 'react';

// ============================================================================
// ICONS
// ============================================================================

const Icons = {
  Save: ({ className = '' }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
    </svg>
  ),
  Building: ({ className = '' }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/>
    </svg>
  ),
  User: ({ className = '' }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  Users: ({ className = '' }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  Check: ({ className = '' }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  X: ({ className = '' }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  Info: ({ className = '' }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
    </svg>
  ),
  ChevronDown: ({ className = '' }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  )
};

// ============================================================================
// API HELPER
// ============================================================================

const API_BASE = process.env.REACT_APP_API_URL || '';

async function apiCall(endpoint, options = {}) {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers
    }
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.message || error.error || 'API request failed');
  }
  return response.json();
}

// ============================================================================
// MULTI-SELECT COMPONENT
// ============================================================================

const MultiSelect = ({ options, selected, onChange, label, description, isDark }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleOption = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div className="relative">
      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
        {label}
      </label>
      {description && (
        <p className={`text-xs mb-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          {description}
        </p>
      )}

      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-4 py-2.5 text-left rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-200 flex items-center justify-between ${
          isDark
            ? 'bg-slate-700 border border-slate-600 text-white'
            : 'bg-white border border-slate-300 text-slate-900'
        }`}
      >
        <div className="flex flex-wrap gap-1">
          {selected.length === 0 ? (
            <span className={isDark ? 'text-slate-400' : 'text-slate-400'}>Select options...</span>
          ) : (
            selected.slice(0, 3).map(value => {
              const option = options.find(o => o.value === value);
              return (
                <span
                  key={value}
                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    isDark ? 'bg-indigo-900/50 text-indigo-300' : 'bg-indigo-100 text-indigo-700'
                  }`}
                >
                  {option?.label || value}
                </span>
              );
            })
          )}
          {selected.length > 3 && (
            <span className={`px-2 py-0.5 rounded-full text-xs ${
              isDark ? 'bg-slate-600 text-slate-300' : 'bg-slate-100 text-slate-600'
            }`}>
              +{selected.length - 3} more
            </span>
          )}
        </div>
        <Icons.ChevronDown className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className={`absolute z-20 mt-2 w-full rounded-xl shadow-xl max-h-60 overflow-y-auto ${
            isDark ? 'bg-slate-700 border border-slate-600' : 'bg-white border border-slate-200'
          }`}>
            {options.map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => toggleOption(option.value)}
                className={`w-full px-4 py-2.5 text-left flex items-center justify-between transition-colors ${
                  selected.includes(option.value)
                    ? isDark ? 'bg-indigo-900/30' : 'bg-indigo-50'
                    : isDark ? 'hover:bg-slate-600' : 'hover:bg-slate-50'
                }`}
              >
                <div>
                  <div className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {option.label}
                  </div>
                  {option.description && (
                    <div className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      {option.description}
                    </div>
                  )}
                </div>
                {selected.includes(option.value) && (
                  <Icons.Check className={isDark ? 'text-indigo-400' : 'text-indigo-600'} />
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ============================================================================
// TOGGLE COMPONENT
// ============================================================================

const Toggle = ({ checked, onChange, label, description, isDark }) => (
  <label className="flex items-start gap-3 cursor-pointer group">
    <div className="relative mt-0.5">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
      <div className={`w-11 h-6 rounded-full transition-colors ${
        checked
          ? 'bg-indigo-600'
          : isDark ? 'bg-slate-600' : 'bg-slate-300'
      }`}>
        <div className={`w-5 h-5 bg-white rounded-full shadow-md transition-transform absolute top-0.5 ${
          checked ? 'translate-x-5' : 'translate-x-0.5'
        }`} />
      </div>
    </div>
    <div>
      <div className={`font-medium group-hover:text-indigo-500 transition-colors ${
        isDark ? 'text-white' : 'text-slate-900'
      }`}>
        {label}
      </div>
      {description && (
        <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          {description}
        </div>
      )}
    </div>
  </label>
);

// ============================================================================
// MAIN SETTINGS PANEL
// ============================================================================

export default function AISettingsPanel({ isAdmin = true, isDarkMode }) {
  // Detect dark mode
  const [isDark, setIsDark] = useState(() => {
    if (typeof isDarkMode === 'boolean') return isDarkMode;
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('kubi_darkMode');
      if (stored !== null) return stored !== 'false';
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  useEffect(() => {
    if (typeof isDarkMode === 'boolean') {
      setIsDark(isDarkMode);
    }
  }, [isDarkMode]);

  const [activeTab, setActiveTab] = useState('organization');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [hasOrgChanges, setHasOrgChanges] = useState(false);
  const [hasUserChanges, setHasUserChanges] = useState(false);

  // Options
  const [options, setOptions] = useState(null);

  // Organization settings
  const [orgProfile, setOrgProfile] = useState({
    businessTypes: [],
    primaryIndustries: [],
    secondaryIndustries: [],
    excludedIndustries: [],
    primaryFunctions: [],
    seniorityFocus: ['mid', 'senior'],
    primaryMarkets: [],
    remoteFriendly: true,
    international: false,
    typicalFeePercentage: 20,
    typicalSalaryRange: { min: 75000, max: 200000 },
    typicalClientSize: []
  });

  // User settings
  const [userPrefs, setUserPrefs] = useState({
    jobFunction: 'recruiting',
    allowKubi: true,
    allowSpirko: false,
    aiPreferences: {
      communicationStyle: 'professional',
      detailLevel: 'balanced',
      proactiveAlerts: true,
      weeklyDigest: true,
      dailyPriorities: true
    }
  });

  // Team members (admin only)
  const [teamMembers, setTeamMembers] = useState([]);

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setIsLoading(true);

      // Load options
      const optionsData = await apiCall('/api/ai/settings/options');
      setOptions(optionsData);

      // Load org settings
      if (isAdmin) {
        const orgData = await apiCall('/api/ai/settings/organization');
        setOrgProfile(orgData.profile);
      }

      // Load user settings
      const userData = await apiCall('/api/ai/settings/user');
      setUserPrefs(userData.preferences);

      // Load team (admin only)
      if (isAdmin) {
        const teamData = await apiCall('/api/ai/settings/team');
        setTeamMembers(teamData.members);
      }
    } catch (err) {
      console.error('Error loading settings:', err);
    } finally {
      setIsLoading(false);
    }
  }

  async function saveOrgSettings() {
    try {
      setIsSaving(true);
      await apiCall('/api/ai/settings/organization', {
        method: 'PUT',
        body: JSON.stringify(orgProfile)
      });
      setSaveMessage({ type: 'success', text: 'Organization settings saved!' });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err) {
      setSaveMessage({ type: 'error', text: err.message });
    } finally {
      setIsSaving(false);
    }
  }

  async function saveUserSettings() {
    try {
      setIsSaving(true);
      await apiCall('/api/ai/settings/user', {
        method: 'PUT',
        body: JSON.stringify(userPrefs)
      });
      setSaveMessage({ type: 'success', text: 'Your preferences saved!' });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err) {
      setSaveMessage({ type: 'error', text: err.message });
    } finally {
      setIsSaving(false);
    }
  }

  // Unified save: saves both org and user settings in one action
  async function saveAllSettings() {
    try {
      setIsSaving(true);
      const promises = [];
      if (isAdmin && hasOrgChanges) {
        promises.push(apiCall('/api/ai/settings/organization', {
          method: 'PUT',
          body: JSON.stringify(orgProfile)
        }));
      }
      if (hasUserChanges) {
        promises.push(apiCall('/api/ai/settings/user', {
          method: 'PUT',
          body: JSON.stringify(userPrefs)
        }));
      }
      if (promises.length > 0) {
        await Promise.all(promises);
        setHasOrgChanges(false);
        setHasUserChanges(false);
        setSaveMessage({ type: 'success', text: 'All settings saved!' });
      } else {
        setSaveMessage({ type: 'success', text: 'No changes to save' });
      }
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err) {
      setSaveMessage({ type: 'error', text: err.message });
    } finally {
      setIsSaving(false);
    }
  }

  // Auto-save on navigate away (unmount)
  useEffect(() => {
    return () => {
      // Fire-and-forget save on unmount if changes pending
      const token = localStorage.getItem('token');
      const API_BASE = process.env.REACT_APP_API_URL || '';
      const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
      if (isAdmin && hasOrgChanges) {
        fetch(`${API_BASE}/api/ai/settings/organization`, {
          method: 'PUT', headers, body: JSON.stringify(orgProfile)
        }).catch(() => {});
      }
      if (hasUserChanges) {
        fetch(`${API_BASE}/api/ai/settings/user`, {
          method: 'PUT', headers, body: JSON.stringify(userPrefs)
        }).catch(() => {});
      }
    };
  }, [hasOrgChanges, hasUserChanges, orgProfile, userPrefs, isAdmin]);

  async function updateTeamMember(userId, updates) {
    try {
      await apiCall(`/api/ai/settings/team/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });
      // Refresh team list
      const teamData = await apiCall('/api/ai/settings/team');
      setTeamMembers(teamData.members);
    } catch (err) {
      console.error('Error updating team member:', err);
    }
  }

  // Wrappers that track changes
  const updateOrgProfile = (newVal) => { setOrgProfile(newVal); setHasOrgChanges(true); };
  const updateUserPrefs = (newVal) => { setUserPrefs(newVal); setHasUserChanges(true); };

  const tabs = [
    ...(isAdmin ? [{ id: 'organization', label: 'Organization', icon: Icons.Building }] : []),
    { id: 'user', label: 'My Preferences', icon: Icons.User },
    ...(isAdmin ? [{ id: 'team', label: 'Team Access', icon: Icons.Users }] : [])
  ];

  if (isLoading) {
    return (
      <div className={`p-8 flex items-center justify-center ${isDark ? 'bg-slate-800' : 'bg-white'} rounded-xl`}>
        <div className="relative w-12 h-12">
          <div className="absolute inset-0 rounded-full border-4 border-t-indigo-500 border-r-transparent border-b-purple-500 border-l-transparent animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-2xl overflow-hidden transition-colors duration-300 ${
      isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-slate-200'
    }`}>
      {/* Tabs */}
      <div className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
        <nav className="flex">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-4 border-b-2 transition-all duration-200 ${
                activeTab === tab.id
                  ? 'border-indigo-500 text-indigo-500'
                  : isDark
                    ? 'border-transparent text-slate-400 hover:text-slate-200'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              <tab.icon />
              <span className="font-medium">{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Save Message */}
      {saveMessage && (
        <div className={`mx-6 mt-4 p-4 rounded-xl flex items-center gap-3 animate-fade-in ${
          saveMessage.type === 'success'
            ? isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-50 text-green-700'
            : isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700'
        }`}>
          {saveMessage.type === 'success' ? <Icons.Check /> : <Icons.X />}
          {saveMessage.text}
        </div>
      )}

      {/* Organization Settings */}
      {activeTab === 'organization' && isAdmin && options && (
        <div className="p-6 space-y-8">
          <div>
            <h2 className={`text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Organization AI Profile
            </h2>
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              Configure your firm's profile to help Roxi and Cooper provide more relevant recommendations
            </p>
          </div>

          {/* Business Model */}
          <div className="grid gap-6 md:grid-cols-2">
            <MultiSelect
              label="Business Types"
              description="What types of recruiting do you do?"
              options={options.businessTypes}
              selected={orgProfile.businessTypes || []}
              onChange={(v) => updateOrgProfile({ ...orgProfile, businessTypes: v })}
              isDark={isDark}
            />

            <MultiSelect
              label="Client Sizes"
              description="What size companies do you typically work with?"
              options={options.clientSizes}
              selected={orgProfile.typicalClientSize || []}
              onChange={(v) => updateOrgProfile({ ...orgProfile, typicalClientSize: v })}
              isDark={isDark}
            />
          </div>

          {/* Industries */}
          <div className="space-y-4">
            <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Industry Focus
            </h3>
            <div className="grid gap-6 md:grid-cols-2">
              <MultiSelect
                label="Primary Industries"
                description="Industries you specialize in"
                options={options.industries}
                selected={orgProfile.primaryIndustries || []}
                onChange={(v) => updateOrgProfile({ ...orgProfile, primaryIndustries: v })}
                isDark={isDark}
              />

              <MultiSelect
                label="Secondary Industries"
                description="Industries you occasionally work in"
                options={options.industries}
                selected={orgProfile.secondaryIndustries || []}
                onChange={(v) => updateOrgProfile({ ...orgProfile, secondaryIndustries: v })}
                isDark={isDark}
              />
            </div>
          </div>

          {/* Job Functions */}
          <div className="grid gap-6 md:grid-cols-2">
            <MultiSelect
              label="Primary Job Functions"
              description="What roles do you typically recruit for?"
              options={options.jobFunctions}
              selected={orgProfile.primaryFunctions || []}
              onChange={(v) => updateOrgProfile({ ...orgProfile, primaryFunctions: v })}
              isDark={isDark}
            />

            <MultiSelect
              label="Seniority Focus"
              description="What levels do you typically place?"
              options={options.seniorityLevels}
              selected={orgProfile.seniorityFocus || []}
              onChange={(v) => updateOrgProfile({ ...orgProfile, seniorityFocus: v })}
              isDark={isDark}
            />
          </div>

          {/* Geographic */}
          <div className="space-y-4">
            <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Geographic Focus
            </h3>
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                  Primary Markets
                </label>
                <input
                  type="text"
                  value={(orgProfile.primaryMarkets || []).join(', ')}
                  onChange={(e) => updateOrgProfile({
                    ...orgProfile,
                    primaryMarkets: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                  })}
                  placeholder="e.g., San Francisco, New York, Austin"
                  className={`w-full px-4 py-2.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-200 ${
                    isDark
                      ? 'bg-slate-700 border border-slate-600 text-white placeholder-slate-400'
                      : 'bg-white border border-slate-300 text-slate-900 placeholder-slate-400'
                  }`}
                />
              </div>

              <div className="flex gap-8">
                <Toggle
                  checked={orgProfile.remoteFriendly !== false}
                  onChange={(v) => updateOrgProfile({ ...orgProfile, remoteFriendly: v })}
                  label="Remote Friendly"
                  description="Include remote candidates in searches by default"
                  isDark={isDark}
                />

                <Toggle
                  checked={orgProfile.international === true}
                  onChange={(v) => updateOrgProfile({ ...orgProfile, international: v })}
                  label="International"
                  description="Include candidates outside the US"
                  isDark={isDark}
                />
              </div>
            </div>
          </div>

          {/* Compensation */}
          <div className="space-y-4">
            <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Compensation Intelligence
            </h3>
            <div className="grid gap-6 md:grid-cols-3">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                  Typical Fee %
                </label>
                <input
                  type="number"
                  value={orgProfile.typicalFeePercentage || 20}
                  onChange={(e) => updateOrgProfile({ ...orgProfile, typicalFeePercentage: parseFloat(e.target.value) })}
                  className={`w-full px-4 py-2.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-200 ${
                    isDark
                      ? 'bg-slate-700 border border-slate-600 text-white'
                      : 'bg-white border border-slate-300 text-slate-900'
                  }`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                  Min Salary Focus
                </label>
                <input
                  type="number"
                  value={orgProfile.typicalSalaryRange?.min || 75000}
                  onChange={(e) => updateOrgProfile({
                    ...orgProfile,
                    typicalSalaryRange: { ...orgProfile.typicalSalaryRange, min: parseInt(e.target.value) }
                  })}
                  className={`w-full px-4 py-2.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-200 ${
                    isDark
                      ? 'bg-slate-700 border border-slate-600 text-white'
                      : 'bg-white border border-slate-300 text-slate-900'
                  }`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                  Max Salary Focus
                </label>
                <input
                  type="number"
                  value={orgProfile.typicalSalaryRange?.max || 200000}
                  onChange={(e) => updateOrgProfile({
                    ...orgProfile,
                    typicalSalaryRange: { ...orgProfile.typicalSalaryRange, max: parseInt(e.target.value) }
                  })}
                  className={`w-full px-4 py-2.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-200 ${
                    isDark
                      ? 'bg-slate-700 border border-slate-600 text-white'
                      : 'bg-white border border-slate-300 text-slate-900'
                  }`}
                />
              </div>
            </div>
          </div>

        </div>
      )}

      {/* User Settings */}
      {activeTab === 'user' && options && (
        <div className="p-6 space-y-8">
          <div>
            <h2 className={`text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Your AI Preferences
            </h2>
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              Customize how Roxi and Cooper work for you
            </p>
          </div>

          {/* Job Function */}
          <div className="space-y-4">
            <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Your Role</h3>
            <div className="grid gap-4 md:grid-cols-3">
              {options.userJobFunctions.map(func => (
                <label
                  key={func.value}
                  className={`p-5 rounded-xl border-2 cursor-pointer transition-all duration-200 hover:scale-[1.02] ${
                    userPrefs.jobFunction === func.value
                      ? 'border-indigo-500 bg-indigo-500/10 shadow-lg shadow-indigo-500/20'
                      : isDark
                        ? 'border-slate-600 hover:border-slate-500'
                        : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="jobFunction"
                    value={func.value}
                    checked={userPrefs.jobFunction === func.value}
                    onChange={(e) => updateUserPrefs({ ...userPrefs, jobFunction: e.target.value })}
                    className="sr-only"
                  />
                  <div className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {func.label}
                  </div>
                  <div className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                    {func.description}
                  </div>
                  <div className="text-xs text-indigo-500 mt-3 font-medium">AI: {func.aiAssistant}</div>
                </label>
              ))}
            </div>
          </div>

          {/* Communication Style */}
          <div className="space-y-4">
            <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Communication Preferences
            </h3>
            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                  Communication Style
                </label>
                <select
                  value={userPrefs.aiPreferences?.communicationStyle || 'professional'}
                  onChange={(e) => updateUserPrefs({
                    ...userPrefs,
                    aiPreferences: { ...userPrefs.aiPreferences, communicationStyle: e.target.value }
                  })}
                  className={`w-full px-4 py-2.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-200 ${
                    isDark
                      ? 'bg-slate-700 border border-slate-600 text-white'
                      : 'bg-white border border-slate-300 text-slate-900'
                  }`}
                >
                  {options.communicationStyles.map(style => (
                    <option key={style.value} value={style.value}>{style.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                  Detail Level
                </label>
                <select
                  value={userPrefs.aiPreferences?.detailLevel || 'balanced'}
                  onChange={(e) => updateUserPrefs({
                    ...userPrefs,
                    aiPreferences: { ...userPrefs.aiPreferences, detailLevel: e.target.value }
                  })}
                  className={`w-full px-4 py-2.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-200 ${
                    isDark
                      ? 'bg-slate-700 border border-slate-600 text-white'
                      : 'bg-white border border-slate-300 text-slate-900'
                  }`}
                >
                  {options.detailLevels.map(level => (
                    <option key={level.value} value={level.value}>{level.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Notifications */}
          <div className="space-y-4">
            <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              AI Notifications
            </h3>
            <div className="space-y-4">
              <Toggle
                checked={userPrefs.aiPreferences?.proactiveAlerts !== false}
                onChange={(v) => updateUserPrefs({
                  ...userPrefs,
                  aiPreferences: { ...userPrefs.aiPreferences, proactiveAlerts: v }
                })}
                label="Proactive Alerts"
                description="Get notified about stale pipelines, hot candidates, and opportunities"
                isDark={isDark}
              />

              <Toggle
                checked={userPrefs.aiPreferences?.dailyPriorities !== false}
                onChange={(v) => updateUserPrefs({
                  ...userPrefs,
                  aiPreferences: { ...userPrefs.aiPreferences, dailyPriorities: v }
                })}
                label="Daily Priorities"
                description="Receive AI-generated daily focus recommendations"
                isDark={isDark}
              />

              <Toggle
                checked={userPrefs.aiPreferences?.weeklyDigest !== false}
                onChange={(v) => updateUserPrefs({
                  ...userPrefs,
                  aiPreferences: { ...userPrefs.aiPreferences, weeklyDigest: v }
                })}
                label="Weekly Digest"
                description="Get a weekly summary of your performance and opportunities"
                isDark={isDark}
              />
            </div>
          </div>

        </div>
      )}

      {/* Team Access */}
      {activeTab === 'team' && isAdmin && (
        <div className="p-6 space-y-6">
          <div>
            <h2 className={`text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Team AI Access
            </h2>
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              Manage which AI assistants each team member can access
            </p>
          </div>

          <div className={`rounded-xl overflow-hidden ${isDark ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
            <table className="w-full">
              <thead className={isDark ? 'bg-slate-700' : 'bg-slate-100'}>
                <tr>
                  <th className={`px-4 py-3 text-left text-sm font-semibold ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                    Team Member
                  </th>
                  <th className={`px-4 py-3 text-left text-sm font-semibold ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                    Role Function
                  </th>
                  <th className={`px-4 py-3 text-center text-sm font-semibold ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                    <div className="flex items-center justify-center gap-1">
                      <div className="w-5 h-5 bg-gradient-to-r from-indigo-500 to-purple-500 rounded text-white text-xs flex items-center justify-center font-bold">R</div>
                      Roxi
                    </div>
                  </th>
                  <th className={`px-4 py-3 text-center text-sm font-semibold ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                    <div className="flex items-center justify-center gap-1">
                      <div className="w-5 h-5 bg-gradient-to-r from-orange-500 to-red-500 rounded text-white text-xs flex items-center justify-center font-bold">C</div>
                      Cooper
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-slate-600' : 'divide-slate-200'}`}>
                {teamMembers.map(member => (
                  <tr key={member.id} className={isDark ? 'bg-slate-800/50' : 'bg-white'}>
                    <td className="px-4 py-3">
                      <div className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {member.name}
                      </div>
                      <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                        {member.email}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={member.jobFunction || 'recruiting'}
                        onChange={(e) => updateTeamMember(member.id, { jobFunction: e.target.value })}
                        className={`px-3 py-1.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                          isDark
                            ? 'bg-slate-700 border border-slate-600 text-white'
                            : 'bg-white border border-slate-300 text-slate-900'
                        }`}
                      >
                        <option value="recruiting">Recruiting</option>
                        <option value="sales">Sales/BD</option>
                        <option value="both">Both</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={member.allowKubi !== false}
                        onChange={(e) => updateTeamMember(member.id, { allowKubi: e.target.checked })}
                        className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500"
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={member.allowSpirko === true}
                        onChange={(e) => updateTeamMember(member.id, { allowSpirko: e.target.checked })}
                        className="w-5 h-5 text-orange-500 rounded focus:ring-orange-500"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={`flex items-start gap-3 p-4 rounded-xl ${
            isDark ? 'bg-blue-900/30 border border-blue-700/50' : 'bg-blue-50 border border-blue-200'
          }`}>
            <Icons.Info className={`flex-shrink-0 mt-0.5 ${isDark ? 'text-blue-400' : 'text-blue-500'}`} />
            <div className={`text-sm ${isDark ? 'text-blue-200' : 'text-blue-800'}`}>
              <strong>Note:</strong> Roxi is the recruiting AI assistant, Cooper is for business development/sales.
              Users with "Both" role function will see both assistants.
            </div>
          </div>
        </div>
      )}

      {/* Unified Save Button - Always visible at bottom */}
      {(hasOrgChanges || hasUserChanges) && (
        <div className={`flex items-center justify-between px-6 py-4 border-t ${isDark ? 'border-slate-700 bg-slate-800/80' : 'border-slate-200 bg-slate-50'}`}>
          <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            {hasOrgChanges && hasUserChanges ? 'Organization & user settings changed' :
             hasOrgChanges ? 'Organization settings changed' : 'User preferences changed'}
            <span className={`ml-2 text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
              (auto-saves on navigate away)
            </span>
          </div>
          <button
            onClick={saveAllSettings}
            disabled={isSaving}
            className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl hover:opacity-90 transition-all duration-200 disabled:opacity-50 shadow-lg shadow-indigo-500/25"
          >
            <Icons.Save />
            {isSaving ? 'Saving...' : 'Save All Settings'}
          </button>
        </div>
      )}

      {/* Save Success/Error Message */}
      {saveMessage && (
        <div className={`px-6 py-3 text-sm font-medium ${
          saveMessage.type === 'success'
            ? isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-50 text-green-700'
            : isDark ? 'bg-red-900/30 text-red-400' : 'bg-red-50 text-red-700'
        }`}>
          {saveMessage.type === 'success' ? <Icons.Check className="inline mr-2 w-4 h-4" /> : null}
          {saveMessage.text}
        </div>
      )}
    </div>
  );
}
